import { Component, HostListener, OnInit, ChangeDetectorRef, effect, untracked } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { AppStateService } from '../../services/app-state.service';
import { environment } from '../../../environments/environment';
import { ExternalLiquidityService } from '../../services/external-liquidity.service';
import { SparklineChartComponent } from '../sparkline-chart/sparkline-chart.component';

interface TopStat {
  title: string;
  value: string;
  change: number;
  isPositive: boolean;
}

@Component({
  selector: 'app-tokens',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './tokens.component.html',
  providers: [DecimalPipe]
})
export class TokensComponent implements OnInit {
  activeTab = 'Tokens'; // default
  searchQuery = '';
  
  // Dropdowns state
  showNetworkDropdown = false;
  networks: any[] = [
    { id: 'all', name: 'All networks', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
    { id: 'eth', name: 'Ethereum', icon: 'eth', chainId: 1 },
    { id: 'bnb', name: 'BNB Chain', icon: 'bnb', chainId: 56 },
    { id: 'matic', name: 'Polygon', icon: 'matic', chainId: 137 },
    { id: 'unichain', name: 'Unichain', icon: 'uni', chainId: 1301 },
    { id: 'base', name: 'Base', icon: 'base', chainId: 8453 },
    { id: 'arbitrum', name: 'Arbitrum', icon: 'arb', chainId: 42161 },
    { id: 'tempo', name: 'Tempo', icon: 'tempo', badge: 'New', chainId: 0 },
    { id: 'monad', name: 'Monad', icon: 'monad', chainId: 0 },
    { id: 'solana', name: 'Solana', icon: 'sol', chainId: 0 }
  ];
  selectedNetwork = this.networks[0];

  showTimeframeDropdown = false;
  timeframes = ['1H volume', '1D volume', '1W volume', '1M volume', '1Y volume', 'All time volume'];
  selectedTimeframe = '1D volume';
  
  // Sorting state
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Pagination & Data
  loadingTokens = false;
  tokens: any[] = [];
  
  // Real Stats
  topStats: TopStat[] = [
    { title: 'Total TVL', value: '$0.00', change: 0, isPositive: true },
    { title: '24H Volume', value: '$0.00', change: 0, isPositive: true },
    { title: 'Total Fees', value: '$0.00', change: 0, isPositive: true },
    { title: 'Avg APR', value: '0.00%', change: 0, isPositive: true },
    { title: 'Total Pools', value: '0', change: 0, isPositive: true }
  ];

  // Pools State
  pools: any[] = [];
  loadingPools = false;

  // Transactions State
  transactions: any[] = [];
  loadingTransactions = false;

  // Pagination totals
  totalTokens = 0;
  totalPools = 0;
  totalTransactions = 0;

  tokensPage = 1;
  tokensLimit = 8;
  
  poolsPage = 1;
  poolsLimit = 8;
  
  transactionsPage = 1;
  transactionsLimit = 8;

  isInitialized = false;

  constructor(
    public appState: AppStateService,
    private externalService: ExternalLiquidityService,
    private cdr: ChangeDetectorRef,
    private decimalPipe: DecimalPipe,
    private route: ActivatedRoute,
    private router: Router
  ) {
    effect(() => {
      const activeNetworkId = this.appState.selectedNetworkId();
      untracked(() => {
        const matchedNetwork = this.networks.find(n => n.id === activeNetworkId);
        if (matchedNetwork) {
          this.selectedNetwork = matchedNetwork;
        } else {
          this.selectedNetwork = this.networks[0];
        }
        if (this.isInitialized) {
          this.resetPagination();
          this.loadAllData();
        }
      });
    });
  }

  ngOnInit() {
    this.route.fragment.subscribe(fragment => {
      if (fragment && ['Tokens', 'Pools', 'Transactions'].includes(fragment)) {
        this.activeTab = fragment;
      } else if (fragment && ['tokens', 'pools', 'transactions'].includes(fragment.toLowerCase())) {
        this.activeTab = fragment.charAt(0).toUpperCase() + fragment.slice(1).toLowerCase();
      }
    });

    const matchedNetwork = this.networks.find(n => n.id === this.appState.selectedNetworkId());
    if (matchedNetwork) {
      this.selectedNetwork = matchedNetwork;
    }
    
    // Load all data from external aggregator once
    this.loadAllData();
    this.isInitialized = true;
  }

  async loadAllData() {
    await Promise.all([
        this.fetchKpiData(),
        this.fetchTokensData(),
        this.fetchPoolsData(),
        this.fetchTransactionsData()
    ]);
  }

  async fetchKpiData() {
    try {
      const chainIds = this.getResolvedChainIds();
      const overview = await this.externalService.getOverview(chainIds);
      if (overview) {
        this.topStats = [
            { title: 'Total TVL', value: `$${this.formatNumber(overview.totalTvl)}`, change: 0, isPositive: true },
            { title: '24H Volume', value: `$${this.formatNumber(overview.totalVolume24h)}`, change: 0, isPositive: true },
            { title: 'Total Fees', value: `$${this.formatNumber(overview.totalFees24h)}`, change: 0, isPositive: true },
            { title: 'Avg APR', value: `${overview.avgApr.toFixed(2)}%`, change: 0, isPositive: true },
            { title: 'Total Pools', value: `${overview.totalPools}`, change: 0, isPositive: true }
        ];
        this.cdr.detectChanges();
      }
    } catch (e) {
      console.error('Failed to load KPIs', e);
    }
  }

  getResolvedChainIds(): string | undefined {
    if (this.selectedNetwork.id === 'all') return undefined;
    return `${this.selectedNetwork.chainId}`;
  }

  async fetchTokensData() {
    this.loadingTokens = true;
    this.cdr.detectChanges();
    try {
        const chainIds = this.getResolvedChainIds();
        const res = await this.externalService.getTokens(
          this.tokensPage, this.tokensLimit, this.searchQuery, chainIds, this.sortColumn, this.sortDirection
        );
        this.tokens = res.data;
        this.totalTokens = res.total;
    } catch (e) {
        console.error('Failed to load tokens', e);
    } finally {
        this.loadingTokens = false;
        this.cdr.detectChanges();
    }
  }

  async fetchPoolsData() {
    this.loadingPools = true;
    this.cdr.detectChanges();
    try {
        const chainIds = this.getResolvedChainIds();
        const res = await this.externalService.getPools(
          this.poolsPage, this.poolsLimit, this.searchQuery, chainIds, this.sortColumn, this.sortDirection
        );
        this.pools = res.data;
        this.totalPools = res.total;
    } catch (e) {
        console.error('Failed to load pools', e);
    } finally {
        this.loadingPools = false;
        this.cdr.detectChanges();
    }
  }

  async fetchTransactionsData() {
    this.loadingTransactions = true;
    this.cdr.detectChanges();
    try {
        const chainIds = this.getResolvedChainIds();
        const res = await this.externalService.getTransactions(
          this.transactionsPage, this.transactionsLimit, chainIds
        );
        this.transactions = res.data;
        this.totalTransactions = res.total;
    } catch (e) {
        console.error('Failed to load txs', e);
    } finally {
        this.loadingTransactions = false;
        this.cdr.detectChanges();
    }
  }

  resetPagination() {
    this.tokensPage = 1;
    this.poolsPage = 1;
    this.transactionsPage = 1;
  }

  formatRelativeTime(timestamp: string): string {
    const d = new Date(timestamp);
    const now = Math.floor(Date.now() / 1000);
    const diff = now - Math.floor(d.getTime() / 1000);
    if (diff < 60) return `${diff} secs ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  }

  formatNumber(num: number): string {
    if (num >= 1e9) {
      return (num / 1e9).toFixed(2) + 'B';
    }
    if (num >= 1e6) {
      return (num / 1e6).toFixed(2) + 'M';
    }
    return this.decimalPipe.transform(num, '1.2-4') || '0.00';
  }

  setTab(tab: string) {
    this.activeTab = tab;
    this.router.navigate([], { fragment: tab, queryParamsHandling: 'preserve' });
  }

  toggleNetworkDropdown() {
    this.showNetworkDropdown = !this.showNetworkDropdown;
    if (this.showNetworkDropdown) this.showTimeframeDropdown = false;
  }

  selectNetwork(network: any) {
    this.selectedNetwork = network;
    this.showNetworkDropdown = false;
    this.resetPagination();
    this.loadAllData();
  }

  toggleTimeframeDropdown() {
    this.showTimeframeDropdown = !this.showTimeframeDropdown;
    if (this.showTimeframeDropdown) this.showNetworkDropdown = false;
  }

  selectTimeframe(tf: string) {
    this.selectedTimeframe = tf;
    this.showTimeframeDropdown = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.network-dropdown-container')) {
      this.showNetworkDropdown = false;
    }
    if (!target.closest('.timeframe-dropdown-container')) {
      this.showTimeframeDropdown = false;
    }
  }

  sortBy(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.resetPagination();
    if (this.activeTab === 'Tokens') this.fetchTokensData();
    else if (this.activeTab === 'Pools') this.fetchPoolsData();
  }

  copyToClipboard(text: string, event: Event) {
    event.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      this.appState.triggerNotification('Address copied to clipboard');
    });
  }

  onSearchChange() {
    this.resetPagination();
    // Only fetch the active tab's data for search performance
    if (this.activeTab === 'Tokens') this.fetchTokensData();
    else if (this.activeTab === 'Pools') this.fetchPoolsData();
    else if (this.activeTab === 'Transactions') this.fetchTransactionsData();
  }

  changePage(direction: number) {
    if (this.activeTab === 'Tokens') {
      if (direction === -1 && this.tokensPage > 1) { this.tokensPage--; this.fetchTokensData(); }
      else if (direction === 1 && this.tokensPage < this.maxTokensPage) { this.tokensPage++; this.fetchTokensData(); }
    } else if (this.activeTab === 'Pools') {
      if (direction === -1 && this.poolsPage > 1) { this.poolsPage--; this.fetchPoolsData(); }
      else if (direction === 1 && this.poolsPage < this.maxPoolsPage) { this.poolsPage++; this.fetchPoolsData(); }
    } else if (this.activeTab === 'Transactions') {
      if (direction === -1 && this.transactionsPage > 1) { this.transactionsPage--; this.fetchTransactionsData(); }
      else if (direction === 1 && this.transactionsPage < this.maxTransactionsPage) { this.transactionsPage++; this.fetchTransactionsData(); }
    }
  }
  
  get maxTokensPage(): number { return Math.max(1, Math.ceil(this.totalTokens / this.tokensLimit)); }
  get maxPoolsPage(): number { return Math.max(1, Math.ceil(this.totalPools / this.poolsLimit)); }
  get maxTransactionsPage(): number { return Math.max(1, Math.ceil(this.totalTransactions / this.transactionsLimit)); }
}
